<template>
  <DataCard
    title="Informations de livraison"
    icon="fas fa-map-marker-alt"
    iconBgColor="bg-green-100"
    iconColor="text-green-600"
    :clickable="false"
    className="max-[600px]:p-4"
  >
    <template #header-toggle v-if="collapsible">
      <button
        type="button"
        class="text-black md:hidden"
        @click.stop="toggleCollapse"
      >
        <i
          :class="[
            'fas transition-transform duration-200',
            isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'
          ]"
        ></i>
      </button>
    </template>
    <!-- Version Desktop - Sans collapse -->
    <div v-if="orderDetailData" class="hidden md:block">
      <div class="grid grid-cols-1 gap-4">
        <!-- Informations de livraison -->
      <div>
        <h4 class="font-medium text-black mb-2 text-sm">Informations de livraison</h4>
        <div class="space-y-1 text-sm">
          <div class="flex justify-between items-center">
            <span class="text-black">Transporteur:</span>
            <div class="flex items-center gap-2">
              <CarrierBadge v-if="carrier && Number(orderDetailData?.livraison?.id_livraison) !== 3 && Number(orderDetailData?.livraison?.id_livraison) !== 2" :carrier="carrier" :show-name="true" />
              <span v-else class="text-black">
                {{ Number(orderDetailData?.livraison?.id_livraison) === 3 ? 'Livraison offerte' : '-' }}
              </span>
            </div>
          </div>
          <div class="flex justify-between">
            <span class="text-black">Service:</span>
            <span class="font-medium text-black">
              {{ carrierServiceName }}
              <strong v-if="isGroupage" class="text-red-600 ml-1">(groupage)</strong>
            </span>
          </div>
          <div class="mt-4 border-t border-gray-200 pt-4">
            <h4 class="font-medium text-black mb-2 text-sm">Adresse de livraison</h4>
            <div class="space-y-2 text-sm">
              <div class="flex justify-between gap-4">
                <span class="text-black">{{ deliveryCompanyLabel }}:</span>
                <span class="font-medium text-black text-right">{{ deliveryAddress.enseigne || '-' }}</span>
              </div>
              <div v-if="deliveryAddress.contact" class="flex justify-between gap-4">
                <span class="text-black">Nom du contact:</span>
                <span class="font-medium text-black text-right">{{ deliveryAddress.contact }}</span>
              </div>
              <div class="flex justify-between gap-4">
                <span class="text-black">Adresse:</span>
                <span class="font-medium text-black text-right">{{ deliveryAddress.adresse || '-' }}</span>
              </div>
              <div class="flex justify-between gap-4">
                <span class="text-black">Code postal:</span>
                <span class="font-medium text-black text-right">{{ deliveryAddress.codePostal || '-' }}</span>
              </div>
              <div class="flex justify-between gap-4">
                <span class="text-black">Ville:</span>
                <span class="font-medium text-black text-right">{{ deliveryAddress.ville || '-' }}</span>
              </div>
              <div class="flex justify-between gap-4">
                <span class="text-black">Pays:</span>
                <span class="font-medium text-black text-right">{{ deliveryCountry || '-' }}</span>
              </div>
              <div class="flex justify-between gap-4">
                <span class="text-black">Téléphone:</span>
                <span class="font-medium text-black text-right">{{ deliveryAddress.telephone || '-' }}</span>
              </div>
            </div>
          </div>
          <div
            v-if="props.carrierMessage"
            class="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-4 text-base text-black"
            v-html="props.carrierMessage"
          ></div>
          <div
            v-if="shouldShowGroupageDate"
            class="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-4"
          >
            <div class="flex items-center justify-between">
              <p class="text-black text-base font-medium">Date de groupage :</p>
              <p class="text-black text-base font-semibold">{{ groupageDateDisplay }}</p>
            </div>
          </div>
          <div
            v-if="parcelDimensions.length || isAdmin"
            class="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-1"
          >
            <div class="flex items-center justify-between gap-3 mb-1">
              <p class="text-black text-base font-medium">Dimensions des colis :</p>
              <button
                v-if="isAdmin"
                type="button"
                :disabled="!canAdminEditDimensions"
                class="px-3 py-1.5 text-xs font-medium border border-blue-500 text-blue-600 rounded-md hover:bg-blue-50 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed"
                :title="adminEditBlockedReason"
                @click="openAdminDimensionsEditor"
              >
                <i class="fas fa-pen mr-1"></i>Modifier
              </button>
            </div>
            <p v-if="!parcelDimensions.length" class="text-black text-sm">
              Aucune dimension renseignée
            </p>
            <p
              v-for="(dim, i) in parcelDimensions"
              :key="i"
              class="text-black text-sm"
            >
              Colis n°{{ i + 1 }} : {{ formatDimension(dim) }}
            </p>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Version Mobile/Tablette avec collapse -->
    <div v-if="orderDetailData" class="block md:hidden">
      <!-- Contenu avec transition pour collapse -->
      <div
        :class="[
          'transition-all duration-300 ease-in-out overflow-hidden',
          collapsible ? (isCollapsed ? 'max-h-0 opacity-0' : 'max-h-screen opacity-100') : ''
        ]"
      >
        <div class="grid grid-cols-1 gap-4">
          <!-- Informations de livraison -->
        <div>
          <h4 class="font-medium text-black mb-2 text-sm">Informations de livraison</h4>
          <div class="space-y-1 text-sm">
            <div class="flex justify-between items-center">
              <span class="text-black">Transporteur:</span>
              <div class="flex items-center gap-2">
                <CarrierBadge v-if="carrier && Number(orderDetailData?.livraison?.id_livraison) !== 3 && Number(orderDetailData?.livraison?.id_livraison) !== 2" :carrier="carrier" :show-name="true" />
                <span v-else class="text-black">
                  {{ Number(orderDetailData?.livraison?.id_livraison) === 3 ? 'Livraison offerte' : '-' }}
                </span>
              </div>
            </div>
            <div class="flex justify-between">
              <span class="text-black">Service:</span>
              <span class="font-medium text-black">
                {{ carrierServiceName }}
                <strong v-if="isGroupage" class="text-red-600 ml-1">(groupage)</strong>
              </span>
            </div>
            <div class="mt-4 border-t border-gray-200 pt-4">
              <h4 class="font-medium text-black mb-2 text-sm">Adresse de livraison</h4>
              <div class="space-y-2 text-sm">
                <div class="flex justify-between gap-4">
                  <span class="text-black">{{ deliveryCompanyLabel }}:</span>
                  <span class="font-medium text-black text-right">{{ deliveryAddress.enseigne || '-' }}</span>
                </div>
                <div v-if="deliveryAddress.contact" class="flex justify-between gap-4">
                  <span class="text-black">Nom du contact:</span>
                  <span class="font-medium text-black text-right">{{ deliveryAddress.contact }}</span>
                </div>
                <div class="flex justify-between gap-4">
                  <span class="text-black">Adresse:</span>
                  <span class="font-medium text-black text-right">{{ deliveryAddress.adresse || '-' }}</span>
                </div>
                <div class="flex justify-between gap-4">
                  <span class="text-black">Code postal:</span>
                  <span class="font-medium text-black text-right">{{ deliveryAddress.codePostal || '-' }}</span>
                </div>
                <div class="flex justify-between gap-4">
                  <span class="text-black">Ville:</span>
                  <span class="font-medium text-black text-right">{{ deliveryAddress.ville || '-' }}</span>
                </div>
                <div class="flex justify-between gap-4">
                  <span class="text-black">Pays:</span>
                  <span class="font-medium text-black text-right">{{ deliveryCountry || '-' }}</span>
                </div>
                <div class="flex justify-between gap-4">
                  <span class="text-black">Téléphone:</span>
                  <span class="font-medium text-black text-right">{{ deliveryAddress.telephone || '-' }}</span>
                </div>
              </div>
            </div>
            <div
              v-if="props.carrierMessage"
              class="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-4 text-base text-black"
              v-html="props.carrierMessage"
            ></div>
            <div
              v-if="shouldShowGroupageDate"
              class="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-4"
            >
              <div class="flex items-center justify-between">
                <p class="text-black text-base font-medium">Date de groupage :</p>
                <p class="text-black text-base font-semibold">{{ groupageDateDisplay }}</p>
              </div>
            </div>
            <div
              v-if="parcelDimensions.length || isAdmin"
              class="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-1"
            >
              <div class="flex items-center justify-between gap-3 mb-1">
                <p class="text-black text-base font-medium">Dimensions des colis :</p>
                <button
                  v-if="isAdmin"
                  type="button"
                  :disabled="!canAdminEditDimensions"
                  class="px-3 py-1.5 text-xs font-medium border border-blue-500 text-blue-600 rounded-md hover:bg-blue-50 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed"
                  :title="adminEditBlockedReason"
                  @click="openAdminDimensionsEditor"
                >
                  <i class="fas fa-pen mr-1"></i>Modifier
                </button>
              </div>
              <p v-if="!parcelDimensions.length" class="text-black text-sm">
                Aucune dimension renseignée
              </p>
              <p
                v-for="(dim, i) in parcelDimensions"
                :key="i"
                class="text-black text-sm"
              >
                Colis n°{{ i + 1 }} : {{ formatDimension(dim) }}
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
    </div>

    <div v-if="!orderDetailData" class="text-sm text-black">
      Chargement des données...
    </div>

    <ModalComp
      v-model:isVisible="showAdminDimensionsEditor"
      title="Modifier les dimensions des colis"
      size="lg"
    >
      <template #body>
        <div
          v-if="hasExistingLabels"
          class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800"
        >
          <i class="fas fa-triangle-exclamation mr-2"></i>
          Des étiquettes existent déjà. Après la modification, elles devront être régénérées.
        </div>

        <div class="space-y-4">
          <div
            v-for="(colis, index) in editableParcelDimensions"
            :key="index"
            class="border border-gray-200 rounded-lg p-4"
          >
            <div class="flex items-center justify-between mb-3">
              <span class="font-medium text-black">Colis n°{{ index + 1 }}</span>
              <button
                v-if="editableParcelDimensions.length > 1"
                type="button"
                class="text-sm text-red-600 hover:text-red-700"
                @click="removeEditableParcel(index)"
              >
                <i class="fas fa-trash-alt mr-1"></i>Supprimer
              </button>
            </div>

            <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
              <label class="text-sm text-black">
                Longueur (cm)
                <input
                  v-model.number="colis.longueur"
                  type="number"
                  min="0"
                  step="0.1"
                  class="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </label>
              <label class="text-sm text-black">
                Largeur (cm)
                <input
                  v-model.number="colis.largeur"
                  type="number"
                  min="0"
                  step="0.1"
                  class="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </label>
              <label class="text-sm text-black">
                Hauteur (cm)
                <input
                  v-model.number="colis.hauteur"
                  type="number"
                  min="0"
                  step="0.1"
                  class="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </label>
              <label class="text-sm text-black">
                Poids (kg)
                <input
                  v-model.number="colis.poids"
                  type="number"
                  min="0"
                  step="0.01"
                  class="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </label>
            </div>
          </div>
        </div>

        <button
          type="button"
          class="mt-4 px-3 py-2 text-sm border border-blue-500 text-blue-600 rounded-md hover:bg-blue-50"
          @click="addEditableParcel"
        >
          <i class="fas fa-plus mr-1"></i>Ajouter un colis
        </button>

        <div
          v-if="dimensionValidationErrors.length"
          class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
        >
          <p v-for="message in dimensionValidationErrors" :key="message">
            {{ message }}
          </p>
        </div>
        <div
          v-if="dimensionLimitWarnings.length"
          class="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800"
        >
          <p class="font-semibold mb-1">
            Dépassement pouvant entraîner une surtaxe :
          </p>
          <p v-for="message in dimensionLimitWarnings" :key="message">
            {{ message }}
          </p>
        </div>
      </template>

      <template #footer>
        <button
          type="button"
          class="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          :disabled="isSavingDimensions"
          @click="showAdminDimensionsEditor = false"
        >
          Annuler
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          :disabled="!canSaveAdminDimensions || isSavingDimensions"
          @click="saveAdminDimensions"
        >
          <i v-if="isSavingDimensions" class="fas fa-spinner fa-spin mr-1"></i>
          {{ isSavingDimensions ? 'Sauvegarde...' : 'Sauvegarder' }}
        </button>
      </template>
    </ModalComp>
  </DataCard>
</template>

<script setup lang="ts">
import { inject, computed, ref, type ComputedRef } from 'vue'
import { DataCard, CarrierBadge, ModalComp } from '@/components'
import type { OrderDetailResponse, CarrierType, ParcelDimension } from '@/types'
import { mapCarrierIdToType } from '@/utils/carrierMapper'
import { formatDateFR } from '@/utils/date'
import { useAuthStore } from '@/stores/auth'
import { useNotification } from '@/composables/useNotification'
import { adminSaveParcelDimensions } from '@/api/orders'
import { mapToDeliveryAddress } from '@/utils/orderMapper'
import { getDomTomNameFromPostalCode } from '@/utils/domTom'
import {
  getCarrierDimensionLimits,
  validateColisDimensions,
} from '@/utils/parcelDimensionsLimits'

// Props
interface Props {
  orderId: number
  collapsible?: boolean
  carrierMessage?: string | null
  hasExistingLabels?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  collapsible: false,
  hasExistingLabels: false,
})

const emit = defineEmits<{
  collapsed: [value: boolean]
}>()

// État du collapse
const isCollapsed = ref(true)

// Récupérer les données depuis le parent via inject
const orderDetailDataInjected = inject<ComputedRef<{ value: OrderDetailResponse | null }>>('orderDetailData', computed(() => ({ value: null })))
const orderDetailData = computed(() => orderDetailDataInjected.value.value)
const reloadOrderData = inject<() => Promise<boolean>>('reloadOrderData')
const authStore = useAuthStore()
const { success, error: showError } = useNotification()
const isAdmin = computed(() => authStore.isImpersonating)
const showAdminDimensionsEditor = ref(false)
const isSavingDimensions = ref(false)
const editableParcelDimensions = ref<ParcelDimension[]>([])

const deliveryAddress = computed(() =>
  orderDetailData.value
    ? mapToDeliveryAddress(orderDetailData.value)
    : {
        enseigne: '',
        contact: '',
        adresse: '',
        codePostal: '',
        ville: '',
        pays: '',
        telephone: '',
      },
)

const deliveryCompanyLabel = computed(() =>
  orderDetailData.value?.adresseLivraison?.Societe?.trim()
    ? "Nom de l'enseigne"
    : 'Nom de la société',
)

const deliveryCountry = computed(() => {
  const country = orderDetailData.value?.adresseLivraison?.pays
  const countryName = country?.texte_fr?.trim() || deliveryAddress.value.pays
  const normalizedCountry = countryName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
  const isDomTom =
    country?.id_pays === 42 ||
    /france\s+dom[\s/-]*tom/.test(normalizedCountry)
  if (!isDomTom) return countryName

  const territory = getDomTomNameFromPostalCode(deliveryAddress.value.codePostal)
  return territory ? `${countryName} (${territory})` : countryName
})

const canAdminEditDimensions = computed(() => {
  const status = Number(orderDetailData.value?.statut)
  return isAdmin.value && status !== 2 && status !== 3
})

const adminEditBlockedReason = computed(() =>
  canAdminEditDimensions.value
    ? 'Modifier les dimensions des colis'
    : 'Modification impossible pour une commande expédiée ou annulée',
)

// Données transporteur
const carrier = computed<CarrierType | undefined>(() => {
  if (!orderDetailData.value?.livraison?.id_livraison) return undefined
  return mapCarrierIdToType(orderDetailData.value.livraison.id_livraison)
})

const carrierServiceName = computed<string>(() => {
  // PRIORITÉ 1: Utiliser le libelle exact depuis la base de données
  if (orderDetailData.value?.livraison?.libelle) {
    return `${orderDetailData.value.livraison.libelle} par Efashion Paris`
  }
  
  // FALLBACK: Utiliser le mapping si le libelle n'est pas disponible
  if (!carrier.value) return '-'
  const serviceNames: Record<CarrierType, string> = {
    colissimo: 'Colissimo par Efashion Paris',
    dpd: 'DPD par Efashion Paris',
    ups: 'UPS par Efashion Paris',
    gpx: 'GPX par Efashion Paris',
    ocx: 'OCX par Efashion Paris',
    chronopost: 'Chronopost par Efashion Paris',
    dhl: 'DHL par Efashion Paris',
    gls: 'GLS par Efashion Paris'
  }
  return serviceNames[carrier.value] || 'Transporteur par Efashion Paris'
})

const isGroupage = computed<boolean>(() => {
  return (
    orderDetailData.value?.commandeGroupe?.groupage === 1 ||
    orderDetailData.value?.groupage === true
  )
})

const shouldShowGroupageDate = computed<boolean>(() => {
  const dateExpedition = orderDetailData.value?.dateExpedition
  const idLivraison = Number(orderDetailData.value?.livraison?.id_livraison || 0)
  return (
    isGroupage.value &&
    !!dateExpedition &&
    dateExpedition !== '0000-00-00 00:00:00' &&
    idLivraison !== 1
  )
})

const groupageDateDisplay = computed<string>(() => {
  const dateExpedition = orderDetailData.value?.dateExpedition
  if (!dateExpedition || dateExpedition === '0000-00-00 00:00:00') return '-'
  try {
    return formatDateFR(dateExpedition)
  } catch {
    return dateExpedition
  }
})

// Dimensions des colis saisies par le grossiste, stockées dans
// commandes.data.infocolis.dimensioncolis (affichage en lecture seule).
const normalizeParcelDimension = (
  dim: Record<string, unknown> | unknown[],
): ParcelDimension => {
  const values = Array.isArray(dim)
    ? dim
    : [dim.longueur, dim.largeur, dim.hauteur, dim.poids]
  const numberOrNull = (value: unknown) => {
    const parsed = Number(value)
    return value !== '' && value != null && Number.isFinite(parsed)
      ? parsed
      : null
  }
  return {
    longueur: numberOrNull(values[0]),
    largeur: numberOrNull(values[1]),
    hauteur: numberOrNull(values[2]),
    poids: numberOrNull(values[3]),
  }
}

const parcelDimensions = computed<Array<Record<string, unknown> | unknown[]>>(() => {
  const raw: unknown = (orderDetailData.value as { data?: unknown } | null)?.data
  if (!raw) return []
  let parsed: unknown = raw
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw)
    } catch {
      return []
    }
  }
  const infocolis = (parsed as { infocolis?: { dimensioncolis?: unknown; nbcolis?: unknown } } | null)?.infocolis
  const list = infocolis?.dimensioncolis
  if (!Array.isArray(list)) return []
  const nbColis = Number(infocolis?.nbcolis) || list.length
  return list.slice(0, nbColis)
})

const formatDimension = (dim: Record<string, unknown> | unknown[]): string => {
  const isArray = Array.isArray(dim)
  const obj = dim as Record<string, unknown>
  const arr = dim as unknown[]
  const longueur = isArray ? arr[0] : obj?.longueur
  const largeur = isArray ? arr[1] : obj?.largeur
  const hauteur = isArray ? arr[2] : obj?.hauteur
  const poids = isArray ? arr[3] : obj?.poids
  const size = `Long. ${longueur ?? '?'} × Larg. ${largeur ?? '?'} × Haut. ${hauteur ?? '?'} cm`
  return poids != null && poids !== '' ? `${size} — ${poids} kg` : size
}

const openAdminDimensionsEditor = () => {
  if (!canAdminEditDimensions.value) return
  editableParcelDimensions.value = parcelDimensions.value.length
    ? parcelDimensions.value.map(normalizeParcelDimension)
    : Array.from(
        { length: Math.max(1, Number(orderDetailData.value?.nb_colis) || 1) },
        () => ({ longueur: null, largeur: null, hauteur: null, poids: null }),
      )
  showAdminDimensionsEditor.value = true
}

const addEditableParcel = () => {
  editableParcelDimensions.value.push({
    longueur: null,
    largeur: null,
    hauteur: null,
    poids: null,
  })
}

const removeEditableParcel = (index: number) => {
  if (editableParcelDimensions.value.length <= 1) return
  editableParcelDimensions.value.splice(index, 1)
}

const dimensionValidationErrors = computed(() => {
  const errors: string[] = []
  editableParcelDimensions.value.forEach((colis, index) => {
    const number = index + 1
    if (
      colis.longueur == null ||
      colis.longueur <= 0 ||
      colis.largeur == null ||
      colis.largeur <= 0 ||
      colis.hauteur == null ||
      colis.hauteur <= 0 ||
      colis.poids == null ||
      colis.poids <= 0
    ) {
      errors.push(`Colis n°${number} : tous les champs doivent être supérieurs à 0.`)
    }
  })
  return errors
})

const dimensionLimitWarnings = computed(() => {
  const warnings: string[] = []
  const limits = getCarrierDimensionLimits(
    Number(orderDetailData.value?.livraison?.id_livraison) || null,
    orderDetailData.value?.adresseLivraison?.pays?.code,
    orderDetailData.value?.adresseLivraison?.codePostal,
  )
  editableParcelDimensions.value.forEach((colis, index) => {
    const number = index + 1
    if (
      colis.longueur == null ||
      colis.longueur <= 0 ||
      colis.largeur == null ||
      colis.largeur <= 0 ||
      colis.hauteur == null ||
      colis.hauteur <= 0 ||
      colis.poids == null ||
      colis.poids <= 0
    ) return

    if (colis.poids > limits.poids) {
      warnings.push(
        `Colis n°${number} : ${colis.poids} kg saisis, maximum ${limits.poids} kg.`,
      )
    }
    const dimensionWarning = validateColisDimensions(
      colis.longueur,
      colis.largeur,
      colis.hauteur,
      limits,
    )
    if (dimensionWarning) {
      warnings.push(`Colis n°${number} : ${dimensionWarning} — surtaxe possible.`)
    }
  })
  return warnings
})

const canSaveAdminDimensions = computed(
  () =>
    canAdminEditDimensions.value &&
    editableParcelDimensions.value.length > 0 &&
    dimensionValidationErrors.value.length === 0,
)

const savedDimensionsMatch = (expected: ParcelDimension[]): boolean => {
  const actual = parcelDimensions.value.map(normalizeParcelDimension)
  if (actual.length !== expected.length) return false

  return expected.every((expectedColis, index) =>
    (['longueur', 'largeur', 'hauteur', 'poids'] as const).every(
      (field) => Number(actual[index]?.[field]) === Number(expectedColis[field]),
    ),
  )
}

const saveAdminDimensions = async () => {
  if (!canSaveAdminDimensions.value || isSavingDimensions.value) return
  if (
    dimensionLimitWarnings.value.length > 0 &&
    !window.confirm(
      `Ces colis dépassent les seuils du transporteur et peuvent entraîner une surtaxe :\n\n${dimensionLimitWarnings.value.join('\n')}\n\nContinuer quand même ?`,
    )
  ) return
  isSavingDimensions.value = true
  let dimensionsSaved = false
  try {
    const expectedDimensions = editableParcelDimensions.value.map((colis) => ({
      ...colis,
    }))
    await adminSaveParcelDimensions(props.orderId, expectedDimensions)
    dimensionsSaved = true

    const refreshed = reloadOrderData ? await reloadOrderData() : false
    if (!refreshed || !savedDimensionsMatch(expectedDimensions)) {
      throw new Error(
        'Les données affichées ne correspondent pas aux dimensions enregistrées.',
      )
    }

    showAdminDimensionsEditor.value = false
    success(
      props.hasExistingLabels
        ? 'Dimensions enregistrées. Les étiquettes doivent être régénérées.'
        : 'Dimensions des colis enregistrées.',
    )
  } catch (err) {
    if (dimensionsSaved) {
      showAdminDimensionsEditor.value = false
      success(
        props.hasExistingLabels
          ? 'Dimensions enregistrées. Les étiquettes doivent être régénérées.'
          : 'Dimensions des colis enregistrées.',
      )
      showError(
        'Les dimensions ont été enregistrées, mais l’affichage n’a pas pu être actualisé. Rechargez la page.',
      )
      return
    }
    showError(
      err instanceof Error
        ? err.message
        : 'Impossible d’enregistrer les dimensions des colis.',
    )
  } finally {
    isSavingDimensions.value = false
  }
}

// Charger les adresses depuis les données de l'API
const toggleCollapse = () => {
  if (props.collapsible) {
    isCollapsed.value = !isCollapsed.value
    emit('collapsed', isCollapsed.value)
  }
}

</script>
